import { OutputAction, trackOutputAction } from 'analytics';
import { putTextOnClipboard } from 'clipboard';
import Icon, { IconType } from 'components/Icon';
import { useCallback } from 'react';
import { toast } from 'react-toastify';
import { unthemed } from 'tokens';
import Modal from '../..';
import OrganizationAccessManager from '../OrganizationAccessManager';
import UserAccessManager from '../UserAccessManager';
import * as UserAccessStyles from '../UserAccessManager/styles';
import * as S from '../styles';

interface CopyLinkConfig {
	shareUrl: string;
	linkLabel: string;
	canNativeShare: boolean;
	shareTitle?: string;
	trackAction: OutputAction;
}

interface UserAccessConfig {
	entityId: string;
	getUsers: (id: string) => Promise<{ emails: string[] }>;
	grantAccess: (id: string, emails: string[]) => Promise<void>;
	revokeAccess: (id: string, email: string) => Promise<void>;
	errorMessages: { fetchFailed: string; revokeFailed: string };
	onAccessGranted?: () => void;
}

interface OrganizationAccessConfig {
	entityId: string;
	getOrganizations: (
		id: string
	) => Promise<{ organizations: { organizationId: string; organizationName: string }[] }>;
	addToOrganization: (entityId: string, orgId: string) => Promise<void>;
	removeFromOrganization: (entityId: string, orgId: string) => Promise<void>;
	emptyStateMessage?: string;
	fetchFailedMessage?: string;
}

interface AccessManagerModalProps {
	title: string;
	onBack?: () => void;
	onClose: () => void;
	userAccess: UserAccessConfig;
	organizationAccess?: OrganizationAccessConfig;
	copyLink?: CopyLinkConfig;
}

const AccessManagerModal = ({
	title,
	onBack,
	onClose,
	userAccess,
	organizationAccess,
	copyLink,
}: AccessManagerModalProps) => {
	const close = useCallback(() => {
		onClose();
	}, [onClose]);

	const onCopyLink = useCallback(async () => {
		if (!copyLink) return;
		const url = copyLink.shareUrl;

		if (copyLink.canNativeShare) {
			try {
				await navigator.share({
					title: copyLink.shareTitle ?? '',
					url,
				});
				trackOutputAction(copyLink.trackAction);
				return;
			} catch (error) {
				if ((error as DOMException)?.name === 'AbortError') {
					return;
				}
				console.warn('Native share failed, falling back to copy', error);
			}
		}

		try {
			await putTextOnClipboard(url);
			trackOutputAction(copyLink.trackAction);
			toast.success('Link copied');
		} catch (error) {
			toast.error('Copy failed — please copy manually');
			console.error('Failed to copy link to clipboard', error);
		}
	}, [copyLink]);

	return (
		<Modal title={title} cancelLabel="Close" submitLabel="Save" onBack={onBack} onClose={close}>
			<>
				<UserAccessManager
					entityId={userAccess.entityId}
					getUsers={userAccess.getUsers}
					grantAccess={userAccess.grantAccess}
					revokeAccess={userAccess.revokeAccess}
					onAccessGranted={userAccess.onAccessGranted}
					errorMessages={userAccess.errorMessages}
				/>
				{organizationAccess && (
					<OrganizationAccessManager
						entityId={organizationAccess.entityId}
						getOrganizations={organizationAccess.getOrganizations}
						addToOrganization={organizationAccess.addToOrganization}
						removeFromOrganization={organizationAccess.removeFromOrganization}
						emptyStateMessage={organizationAccess.emptyStateMessage}
						fetchFailedMessage={organizationAccess.fetchFailedMessage}
					/>
				)}
				{copyLink && (
					<>
						<UserAccessStyles.Divider />
						<S.LinkSection>
							<S.LinkButton onClick={onCopyLink}>
								<Icon
									type={copyLink.canNativeShare ? IconType.SHARE : IconType.COPY}
									color={unthemed.gray['0']}
									width={14}
									height={14}
								/>
								<S.Link>{copyLink.linkLabel}</S.Link>
							</S.LinkButton>
						</S.LinkSection>
					</>
				)}
			</>
		</Modal>
	);
};

export default AccessManagerModal;
